import { Highlight } from '@components/about-spotlight/components/highlight';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import type { Decorated } from '@typings/vendor';
import { useAtomValue } from 'jotai';
import React, { useMemo } from 'react';
import {
	languagesAtom,
	startYearAtom,
	teamMembersAtom,
	teamSizeAtom,
	totalMembersAtom,
} from '../../../../jotai/storefront/organization';
import DetailsHelper from '../../../../pages/Storefront/containers/details/helpers';
import Style from './about-highlights.scss';

const formatBusinessAttributes = (businessAttributes: string[]) => {
	if (businessAttributes.length === 1) return businessAttributes[0];

	const lastElement = businessAttributes[businessAttributes.length - 1];
	const otherElements = businessAttributes.slice(
		0,
		businessAttributes.length - 1,
	);

	const formattedOthers = otherElements.map((attr) =>
		attr.replace(' Business', ''),
	);

	return `${formattedOthers.join(', ')} and ${lastElement}`;
};

export const AboutHighlights = () => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const currentYear = new Date().getFullYear();
	const startYear = useAtomValue(startYearAtom);
	const totalMembers = useAtomValue(totalMembersAtom);
	const teamSize = useAtomValue(teamSizeAtom);
	const languages = useAtomValue(languagesAtom)?.join(', ');
	const hasTeamMembers = useAtomValue(teamMembersAtom)?.length > 0;
	const isLiteStorefront = useLiteStorefront();

	const yearsInBusiness = startYear ? currentYear - startYear : 0;

	const flattenedDetails = DetailsHelper.detailsFlattener(vendor) as {
		'Business Attributes': string[];
	};
	const businessAttributes = flattenedDetails['Business Attributes'];

	const teamSizeText = useMemo(() => {
		if (!teamSize && !totalMembers) return null;
		return teamSize ?? `${totalMembers} team members`;
	}, [teamSize, totalMembers]);

	if (
		businessAttributes?.length > 0 ||
		yearsInBusiness ||
		languages ||
		totalMembers ||
		teamSize
	) {
		return (
			<div className={Style.container} data-testid="about-highlights">
				{businessAttributes?.length > 0 && (
					<Highlight
						textWrapper="body2"
						icon="store"
						text={formatBusinessAttributes(businessAttributes)}
					/>
				)}
				{yearsInBusiness > 0 && (
					<Highlight
						textWrapper="body2"
						icon="calendar"
						text={`${yearsInBusiness}+ year${
							yearsInBusiness > 1 ? 's' : ''
						} in business`}
					/>
				)}
				{languages && (
					<Highlight
						textWrapper="body2"
						icon="speech-bubble-text"
						text={`Speaks ${languages}`}
					/>
				)}
				{teamSizeText && (
					<Highlight
						textWrapper="body2"
						icon="couple"
						text={teamSizeText}
						linkHref={hasTeamMembers ? '#navTeam' : undefined}
					/>
				)}
			</div>
		);
	}

	if (!isLiteStorefront) {
		return (
			<div>
				<Highlight
					textWrapper="body2"
					icon="store"
					text="No business details yet"
				/>
				<Highlight
					textWrapper="body2"
					icon="couple"
					text="No team details yet"
				/>
			</div>
		);
	}

	return null;
};
